import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { NUM, fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import HtmlPreviewDialog, { fetchPreviewUrl, buildIncomingHtml } from "@/components/HtmlPreviewDialog";
import { Loader2, TrendingUp, TrendingDown, Minus, Eye, CalendarClock, FileText, Building2, Boxes, ArrowDownUp, Scale, ScanSearch, CalendarRange } from "lucide-react";
import { Input } from "@/components/ui/input";
import { money, kpis, valid, PERIODS } from "./purchaseApi";

const TH = "py-2 px-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50 border-b whitespace-nowrap sticky top-0 z-10";
const TD = "py-2 px-3 border-b border-border/60 align-top";

/** Fatura önizleme: gelen e-fatura → QNB görüntüsü (yoksa yerel), elle girilen → ekli belge, yoksa Alış Faturaları'nda açılır. */
export function useDocOpen() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();
  const open = async (r, key) => {
    setBusy(key);
    try {
      if (r.incoming_id) {
        try { setPreview({ url: await fetchPreviewUrl(`/qnb/incoming/${r.incoming_id}/document`), title: `${r.belge_no} · ${r.supplier}` }); }
        catch { const { data } = await api.get(`/qnb/incoming/${r.incoming_id}`); setPreview({ html: buildIncomingHtml(data), title: r.belge_no }); }
      } else if (r.doc_url) setPreview({ url: r.doc_url, title: `${r.belge_no} · ${r.supplier}` });
      else nav(`/alis?q=${encodeURIComponent(r.belge_no || r.supplier)}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };
  const dialog = <HtmlPreviewDialog open={!!preview} title={preview?.title} url={preview?.url} html={preview?.html} onClose={() => setPreview(null)} />;
  return { open, busy, dialog };
}

export function SourceBadge({ r }) {
  const cls = "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap";
  if (r.status === "red") return <span className={cn(cls, "bg-red-50 text-red-700")}>Reddedildi</span>;
  if (r.source === "manuel") return <span className={cn(cls, "bg-slate-100 text-slate-700")}>Elle girilen</span>;
  if (r.status === "kabul" || r.status === "kayitli") return <span className={cn(cls, "bg-sky-50 text-sky-700")}>e-Fatura · kabul</span>;
  return <span className={cn(cls, "bg-amber-50 text-amber-800")} title="Gelen e-fatura henüz kabul edilmedi — alış geçmişinde yine de görünür">e-Fatura · yanıt bekliyor</span>;
}

// Dönem süzgeci: Tümü · Aylık · 3 / 6 Aylık · Yıllık · Bu Yıl · Tarih aralığı
export function PeriodFilter({ value, onChange, compact }) {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="ph-period">
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground shrink-0"><CalendarRange className="h-3.5 w-3.5" />Dönem:</span>
      {PERIODS.map(([k, l]) => (
        <button key={k} type="button" onClick={() => set({ period: k })} data-testid={`ph-period-${k}`}
          className={cn("px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors", value.period === k ? "bg-[#EA5820] text-white shadow-sm" : "bg-muted text-muted-foreground hover:bg-muted/70")}>{l}</button>
      ))}
      {value.period === "custom" && (
        <div className={cn("flex items-center gap-1.5", compact ? "w-full" : "")}>
          <Input type="date" value={value.from || ""} max={value.to || undefined} onChange={(e) => set({ from: e.target.value })} className="h-8 w-[150px] text-xs" data-testid="ph-period-from" />
          <span className="text-xs text-muted-foreground">–</span>
          <Input type="date" value={value.to || ""} min={value.from || undefined} onChange={(e) => set({ to: e.target.value })} className="h-8 w-[150px] text-xs" data-testid="ph-period-to" />
        </div>
      )}
    </div>
  );
}

function Trend({ v }) {
  if (v == null) return <span className="text-muted-foreground">—</span>;
  if (Math.abs(v) < 0.5) return <span className="inline-flex items-center gap-0.5 text-muted-foreground"><Minus className="h-3 w-3" />aynı</span>;
  const up = v > 0;
  return <span className={cn("inline-flex items-center gap-0.5 font-bold tnum", up ? "text-red-600" : "text-emerald-600")}>{up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{NUM(Math.round(v * 10) / 10)}%</span>;
}

function Kpi({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div className={cn("rounded-xl border bg-card px-3 py-2.5 min-w-0", accent && "border-primary/40 bg-primary/5")}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="font-head font-black text-[15px] tnum truncate mt-0.5" data-testid={testid} title={typeof value === "string" ? value : undefined}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate" title={typeof sub === "string" ? sub : undefined}>{sub}</div>}
    </div>
  );
}

export function KpiStrip({ rows, compact }) {
  const k = kpis(rows);
  if (!k) return null;
  const one = k.units.length === 1;
  return (
    <div className={cn("grid gap-2", compact ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2 md:grid-cols-3 xl:grid-cols-6")} data-testid="ph-kpis">
      <Kpi icon={CalendarClock} label="Son alış" value={fmtDate(k.last.date)} sub={k.last.supplier} testid="ph-kpi-last" accent />
      <Kpi icon={Scale} label="Son net fiyat" value={money(k.last.net_unit, k.last.currency)} sub={`${k.last.belge_no} · ${NUM(k.last.quantity)} ${k.last.unit}`} testid="ph-kpi-last-price" />
      <Kpi icon={FileText} label="Fatura" value={NUM(k.invoices)} sub={`${NUM(valid(rows).length)} kalem`} testid="ph-kpi-invoices" />
      <Kpi icon={Building2} label="Tedarikçi" value={NUM(k.suppliers)} testid="ph-kpi-suppliers" />
      <Kpi icon={Boxes} label="Toplam alınan" value={one ? `${NUM(k.qty)} ${k.units[0]}` : "—"} sub={one ? null : `farklı birimler: ${k.units.join(", ")}`} testid="ph-kpi-qty" />
      <Kpi icon={ArrowDownUp} label="En düşük / en yüksek" value={`${money(k.min, k.last.currency)} / ${money(k.max, k.last.currency)}`} sub={`ağırlıklı ort. ${money(k.avg, k.last.currency)}`} testid="ph-kpi-range" />
    </div>
  );
}

export function SupplierTable({ stats, active, onPick, compact }) {
  if (!stats.length) return null;
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="ph-suppliers">
      <div className="px-3 py-2 border-b text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Tedarikçiler · son alıştan eskiye{onPick && <span className="normal-case font-normal tracking-normal ml-1">(tıklayınca süzülür)</span>}</div>
      <div className="overflow-x-auto max-h-64">
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead><tr>{["Tedarikçi", "Fatura", "Toplam miktar", "Son alış", "Son net fiyat", ...(compact ? [] : ["En düşük", "En yüksek"])].map((h, i) => <th key={h} className={cn(TH, i > 0 && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {stats.map((s, i) => (
              <tr key={s.key} onClick={onPick ? () => onPick(s.key) : undefined} className={cn(onPick && "cursor-pointer hover:bg-muted/40", active === s.key && "bg-amber-50")} data-testid={`ph-supplier-${i}`}>
                <td className={cn(TD, "min-w-[180px]")}><div className="font-semibold leading-snug">{s.supplier || "—"}</div>{s.vkn && <div className="text-[10px] text-muted-foreground font-mono">VKN/TCKN {s.vkn}</div>}</td>
                <td className={cn(TD, "text-right tnum")}>{NUM(s.inv.size)}</td>
                <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{s.units.size === 1 ? `${NUM(s.qty)} ${[...s.units][0]}` : NUM(s.qty)}</td>
                <td className={cn(TD, "text-right whitespace-nowrap")}>{fmtDate(s.last.date)}<div className="text-[10px] text-muted-foreground font-mono">{s.last.belge_no}</div></td>
                <td className={cn(TD, "text-right tnum font-bold text-emerald-700 whitespace-nowrap")}>{money(s.last.net_unit, s.last.currency)}</td>
                {!compact && <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{money(s.min, s.last.currency)}</td>}
                {!compact && <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{money(s.max, s.last.currency)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function HistoryTable({ rows, doc, compact }) {
  const heads = ["Tarih", "Tedarikçi", "Fatura No", "Kaynak", ...(compact ? [] : ["Ürün (faturadaki)"]), "Miktar", "Birim fiyat", "İsk.", "Net birim", "Değişim", ...(compact ? [] : ["KDV"]), "Tutar", ""];
  const right = new Set(["Miktar", "Birim fiyat", "İsk.", "Net birim", "Değişim", "KDV", "Tutar"]);
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="ph-lines">
      <div className="px-3 py-2 border-b text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Alış faturaları · {NUM(rows.length)} kalem</div>
      <div className={cn("overflow-auto", compact ? "max-h-80" : "max-h-[60vh]")}>
        <table className="w-full text-[12px] border-separate border-spacing-0">
          <thead><tr>{heads.map((h, i) => <th key={i} className={cn(TH, right.has(h) && "text-right")}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const key = `${r.source}-${r.incoming_id || r.invoice_id}-${r.index}`;
              return (
                <tr key={key} className={cn("hover:bg-muted/30", r.status === "red" && "opacity-60 line-through decoration-red-300")} data-testid={`ph-line-${i}`}>
                  <td className={cn(TD, "whitespace-nowrap font-semibold")}>{fmtDate(r.date)}</td>
                  <td className={cn(TD, "min-w-[160px] max-w-[240px]")}><div className="leading-snug truncate" title={r.supplier}>{r.supplier}</div>{r.vkn && <div className="text-[10px] text-muted-foreground font-mono">{r.vkn}</div>}</td>
                  <td className={cn(TD, "font-mono whitespace-nowrap")}>{r.belge_no || "—"}</td>
                  <td className={TD}><SourceBadge r={r} /></td>
                  {!compact && <td className={cn(TD, "min-w-[220px] max-w-[320px]")}><div className="leading-snug line-clamp-2" title={r.name}>{r.name}</div><div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">{r.code && <span className="font-mono">{r.code}</span>}{r.product_name && r.product_name !== r.name && <span className="text-teal-700">bizde: {r.product_name}</span>}</div></td>}
                  <td className={cn(TD, "text-right tnum whitespace-nowrap font-semibold")}>{NUM(r.quantity)} <span className="text-[10px] text-muted-foreground">{r.unit}</span></td>
                  <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{money(r.unit_price, r.currency)}</td>
                  <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{r.discount_rate ? `%${NUM(r.discount_rate)}` : "—"}</td>
                  <td className={cn(TD, "text-right tnum whitespace-nowrap font-bold text-emerald-700")} data-testid={`ph-line-net-${i}`}>{money(r.net_unit, r.currency)}</td>
                  <td className={cn(TD, "text-right whitespace-nowrap text-[11px]")}><Trend v={r.trend} /></td>
                  {!compact && <td className={cn(TD, "text-right tnum")}>%{NUM(r.tax_rate)}</td>}
                  <td className={cn(TD, "text-right tnum whitespace-nowrap")}>{money(r.net_unit * r.quantity, r.currency)}</td>
                  <td className={cn(TD, "text-right")}>
                    <Button size="icon" variant="ghost" className="h-7 w-7" title="Faturayı aç" onClick={() => doc.open(r, key)} data-testid={`ph-line-open-${i}`}>
                      {doc.busy === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Kalemleri henüz okunmamış gelen e-faturalar için uyarı + arka planda okuma. */
export function ScanNotice({ unscanned, onDone }) {
  const [st, setSt] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  if (!unscanned && !st?.running) return null;
  const start = async () => {
    try {
      const { data } = await api.post("/qnb/returns/scan", {});
      setSt(data);
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        try {
          const { data: s } = await api.get("/qnb/returns/scan-status");
          setSt(s);
          if (!s.running) { clearInterval(timer.current); onDone?.(); }
        } catch { clearInterval(timer.current); }
      }, 4000);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2 flex-wrap" data-testid="ph-unscanned">
      <ScanSearch className="h-4 w-4 shrink-0" />
      <span>{st?.running ? `Gelen faturaların kalemleri okunuyor… ${NUM(st.done || 0)}/${NUM(st.total || 0)}` : `${NUM(unscanned)} gelen e-faturanın kalemleri henüz okunmadı — bu faturalardaki alışlar listede görünmeyebilir.`}</span>
      {!st?.running && <button type="button" onClick={start} className="ml-auto font-bold underline underline-offset-2" data-testid="ph-scan-start">Şimdi oku</button>}
      {st?.running && <Loader2 className="h-3.5 w-3.5 animate-spin ml-auto" />}
    </div>
  );
}
